import styled from '@emotion/styled';
import {CalendarApi} from '@fullcalendar/core';
import React, {
  createContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {EHeaderButton} from 'webapp-lib/Components';
import {mixinFloatingSideBar} from 'webapp-lib/styling';

import {Clock} from '_/assets/images';

import {TimeTracker} from './TimeTracker';

type TimeTrackerShelfProps = {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
};

export type CalendarContextProps = {
  calendarAPI: () => CalendarApi;
  setCalendarAPI: (api: CalendarApi) => void;
  quickActivityInputRef: React.MutableRefObject<HTMLInputElement | null>;
};

export const CalendarAPIContext = createContext<CalendarContextProps>(null!);

export const TimeTrackerShelf = ({
  isOpen,
  setIsOpen,
}: TimeTrackerShelfProps): React.ReactElement => {
  const [isOpenedOnce, setIsOpenedOnce] = useState(false);

  const toggleSideBar = () => {
    setIsOpen(!isOpen);
    setIsOpenedOnce(true);
  };

  return (
    <>
      <EHeaderButton
        isActive={isOpen}
        title='Time Tracker'
        onClick={toggleSideBar}
      >
        <Clock />
      </EHeaderButton>

      <SideBar isOpen={isOpen}>
        {isOpenedOnce ? (
          <CalendarContext setFocus={isOpen}>
            <TimeTracker />
          </CalendarContext>
        ) : null}
      </SideBar>
    </>
  );
};

const CalendarContext = ({
  setFocus,
  children,
}: {
  setFocus: boolean;
  children: React.ReactNode;
}): React.ReactElement => {
  const [calendarApi, setCalendarApi] = useState<CalendarApi>(null!);
  const quickActivityInputRef = useRef(null);

  const store: CalendarContextProps = useMemo(
    () => ({
      calendarAPI: () => calendarApi,
      setCalendarAPI: setCalendarApi,
      quickActivityInputRef,
    }),
    [calendarApi]
  );

  useEffect(() => {
    if (setFocus) {
      store.quickActivityInputRef.current?.focus();
    }
  }, [setFocus, store.quickActivityInputRef]);

  return (
    <CalendarAPIContext.Provider value={store}>
      {children}
    </CalendarAPIContext.Provider>
  );
};

const SideBar = styled.div<{isOpen: boolean}>`
  ${mixinFloatingSideBar};

  width: var(--side-bar-wide-width);
  display: ${({isOpen}): string => (isOpen ? 'block' : 'none')};
`;
